import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, ImageOff, Trash2 } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import QuantityStepper from '../components/QuantityStepper';
import { SkeletonBlock } from '../components/Skeleton';
import EmptyState from '../components/EmptyState';
import usePageMetadata from '../hooks/usePageMetadata';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import * as cartApi from '../api/endpoints/cart';
import { btnLg, btnPrimary, containerContent, focusRing, pageHeading, pageSection, panelHeading } from '../styles/ui';

const CartPage = () => {
  const { items, setQuantity, removeItem } = useCart();
  const { authStatus } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState(null);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const quoteRequestIdRef = useRef(0);

  usePageMetadata({
    title: 'Your Cart | Fresh & Homely',
    description: 'Review the items in your Fresh & Homely cart before checkout.',
    canonicalPath: '/cart',
  });

  useEffect(() => {
    if (items.length === 0 || authStatus !== 'authenticated') {
      ++quoteRequestIdRef.current;
      setQuote(null);
      setLoadingQuote(false);
      setQuoteError(null);
      return undefined;
    }
    setLoadingQuote(true);
    setQuoteError(null);
    let active = true;
    const requestId = ++quoteRequestIdRef.current;
    cartApi
      .quoteCart(items.map((item) => ({ productId: item.productId, quantity: item.quantity })))
      .then((response) => {
        if (!active || requestId !== quoteRequestIdRef.current) return; // superseded by a newer quantity change
        setQuote(response.data);
      })
      .catch((err) => {
        if (!active || requestId !== quoteRequestIdRef.current) return;
        setQuoteError(err.message);
      })
      .finally(() => {
        if (!active || requestId !== quoteRequestIdRef.current) return;
        setLoadingQuote(false);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, authStatus]);

  const issueFor = (productId) => quote?.issues?.find((issue) => issue.productId === productId);

  const handleRemove = (item) => {
    removeItem(item.productId);
    showToast(`${item.product?.displayName || 'Item'} removed from cart`, { tone: 'info' });
  };

  const handleCheckout = () => {
    if (authStatus !== 'authenticated') {
      navigate('/login?redirect=/checkout');
      return;
    }
    navigate('/checkout');
  };

  if (items.length === 0) {
    return (
      <>
        <Navbar />
        <section className={pageSection}>
          <div className={containerContent}>
            <EmptyState
              icon={ShoppingCart}
              title="Your cart is empty"
              description="Browse our categories to find fresh products."
              action={{ label: 'Shop Categories', to: '/categories' }}
            />
          </div>
        </section>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <section className={pageSection}>
        <div className={containerContent}>
          <h1 className={`${pageHeading} mb-8 sm:mb-10`}>Your Cart</h1>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-4">
              {items.map((item) => {
                const issue = issueFor(item.productId);
                return (
                  <div
                    key={item.productId}
                    className="card-hover bg-white rounded-2xl border border-emerald-100 shadow-card p-4 flex gap-4 items-center"
                  >
                    <div className="w-20 h-20 bg-gradient-to-br from-emerald-50 to-emerald-100/50 rounded-xl border border-emerald-100/70 overflow-hidden flex items-center justify-center shrink-0">
                      {item.product?.thumbnail ? (
                        <img src={item.product.thumbnail} alt={item.product.displayName} className="w-full h-full object-cover" />
                      ) : (
                        <ImageOff className="text-emerald-300" size={24} aria-hidden="true" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <Link to={`/products/${item.product?.sku || item.productId}`} className={focusRing}>
                        <h3 className="font-bold text-emerald-950 text-sm truncate">
                          {item.product?.displayName || 'Product'}
                        </h3>
                      </Link>
                      <div className="flex items-baseline gap-1.5">
                        <span className="text-sm font-semibold text-emerald-950">₹{item.product?.price ?? '—'}</span>
                        {item.product?.mrp != null && item.product.mrp > item.product.price && (
                          <span className="text-xs text-gray-400 line-through">₹{item.product.mrp}</span>
                        )}
                        {item.product?.discountPercentage > 0 && (
                          <span className="text-[10px] font-bold text-accent-600 bg-accent-50 px-1.5 py-0.5 rounded-full">
                            {item.product.discountPercentage}% OFF
                          </span>
                        )}
                      </div>
                      {issue && (
                        <p className="text-xs text-amber-700 mt-1">{issue.message}</p>
                      )}
                    </div>
                    <QuantityStepper quantity={item.quantity} onChange={(qty) => setQuantity(item.productId, qty)} />
                    <button
                      type="button"
                      onClick={() => handleRemove(item)}
                      aria-label="Remove item"
                      className={`text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors p-2 rounded-full ${focusRing}`}
                    >
                      <Trash2 size={18} aria-hidden="true" />
                    </button>
                  </div>
                );
              })}
            </div>

            <div className="bg-white rounded-3xl border border-emerald-50 shadow-lifted p-6 h-fit">
              <h2 className={`${panelHeading} mb-4`}>Order Summary</h2>

              {authStatus !== 'authenticated' && (
                <p className="text-sm text-ink-secondary mb-4">Sign in to see accurate pricing and delivery fees.</p>
              )}
              {loadingQuote && (
                <div className="space-y-3 mb-4">
                  <SkeletonBlock className="h-4 w-full" />
                  <SkeletonBlock className="h-4 w-full" />
                  <SkeletonBlock className="h-4 w-full" />
                  <SkeletonBlock className="h-6 w-full" />
                </div>
              )}
              {quoteError && <p className="text-sm text-red-600 mb-4">{quoteError}</p>}

              {quote && (
                <div className="space-y-2 text-sm mb-4">
                  <div className="flex justify-between text-ink-secondary">
                    <span>Subtotal</span>
                    <span>₹{quote.subtotal}</span>
                  </div>
                  <div className="flex justify-between text-ink-secondary">
                    <span>Tax</span>
                    <span>₹{quote.totalTax}</span>
                  </div>
                  <div className="flex justify-between text-ink-secondary">
                    <span>Delivery</span>
                    <span className={quote.isFreeDelivery ? 'text-emerald-700 font-semibold' : ''}>
                      {quote.isFreeDelivery ? 'Free' : `₹${quote.deliveryFee}`}
                    </span>
                  </div>
                  {quote.platformCharge > 0 && (
                    <div className="flex justify-between text-ink-secondary">
                      <span>Platform charge</span>
                      <span>₹{quote.platformCharge}</span>
                    </div>
                  )}
                  {quote.handlingCharge > 0 && (
                    <div className="flex justify-between text-ink-secondary">
                      <span>Handling charge</span>
                      <span>₹{quote.handlingCharge}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-baseline pt-3 mt-1 border-t border-emerald-50">
                    <span className="font-bold text-emerald-950">Total</span>
                    <span className="serif text-2xl text-emerald-950 font-bold">₹{quote.grandTotal}</span>
                  </div>
                </div>
              )}

              <button
                type="button"
                onClick={handleCheckout}
                className={`w-full ${btnPrimary} ${btnLg}`}
              >
                Proceed to Checkout
              </button>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
};

export default CartPage;
